import React, { useState, useEffect, useMemo } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { assetNamesSelector } from '../../features/assets/assetsSlice';
import {
    activeSubAccountSelector,
    selectActiveAsset,
    reloadAssetsSelector,
    reloadAccountsSelector,
    getSubAccountDetails,
    parentSelectedSensorList,
    changeView,
} from '../../features/subaccounts/subaccountSlice';

import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Icon from '@mdi/react';
import { mdiClose, mdiMagnify } from '@mdi/js';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import SubAccountVehicleCard from './SubAccountVehicleCard';
import SubAccountShareAllVehicles from './SubAccountShareAllVehicles';
import { deleteShare } from '../../features/subaccounts/subaccountSlice';

const useStyles = makeStyles(() => ({
    dialogPaper: {
        borderRadius: 18,
        background: 'linear-gradient(135deg, rgba(248,250,252,0.98), rgba(226,232,240,0.95))',
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.2)',
        overflow: 'hidden',
    },
    dialogTitle: {
        padding: '20px 24px',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
        color: '#f8fafc',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    titleText: {
        fontWeight: 700,
        letterSpacing: '0.02em',
    },
    subtitleText: {
        fontSize: '0.8rem',
        color: 'rgba(226, 232, 240, 0.8)',
    },
    searchField: {
        minWidth: 240,
        backgroundColor: 'rgba(248, 250, 252, 0.95)',
        borderRadius: 12,
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(148, 163, 184, 0.4)',
        },
        '& .MuiInputBase-input': {
            fontSize: '0.9rem',
        },
    },
    closeButton: {
        padding: 6,
        borderRadius: 10,
        border: '1px solid rgba(148, 163, 184, 0.4)',
        color: '#e2e8f0',
        backgroundColor: 'rgba(15, 23, 42, 0.35)',
        '&:hover': {
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
        },
    },
    content: {
        padding: 20,
        paddingTop: 36,
    },
    panel: {
        borderRadius: 16,
        border: '1px solid rgba(148, 163, 184, 0.35)',
        backgroundColor: '#ffffff',
        boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        minHeight: 320,
    },
    panelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    panelTitle: {
        fontWeight: 600,
        color: '#0f172a',
    },
    chipsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: 12,
    },
    emptyText: {
        color: '#64748b',
        fontSize: '0.85rem',
    },
    selectAllButton: {
        textTransform: 'none',
        borderRadius: 10,
    },
    panelHeaderActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    panelCloseButton: {
        padding: 6,
        borderRadius: 10,
        border: '1px solid rgba(148, 163, 184, 0.4)',
        color: '#64748b',
        backgroundColor: '#ffffff',
        '&:hover': {
            backgroundColor: '#f1f5f9',
        },
    },
}));

const SubAccountVehiclesList = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const assetNames = useSelector(assetNamesSelector);
    const activeSubAccount = useSelector(activeSubAccountSelector);
    const reloadAssets = useSelector(reloadAssetsSelector);
    const reloadAccounts = useSelector(reloadAccountsSelector);
    const [selectFew, setSelectFew] = useState([]);
    const sharedAssetsById = useMemo(() => {
        const list = {};
        if (activeSubAccount && activeSubAccount.assets) {
            activeSubAccount.assets.forEach((asset) => {
                list[asset.imei] = asset;
            });
        }
        return list;
    }, [activeSubAccount]);

    const handleSelectFew = (assetId) => {
        const thisIndex = selectFew.indexOf(assetId);
        const newList = [...selectFew];
        if (thisIndex === -1) {
            newList.push(assetId);
        } else {
            newList.splice(thisIndex, 1);
        }
        setSelectFew(newList);
    };
    useEffect(() => {
        if (selectFew.length > 0) {
            dispatch(parentSelectedSensorList({ imeis: selectFew }));
        }
    }, [dispatch, selectFew]);

    useEffect(() => {
        const sharedIds = new Set(Object.keys(sharedAssetsById));
        setSelectFew((prev) => prev.filter((assetId) => !sharedIds.has(assetId)));
    }, [sharedAssetsById]);

    const [search, setSearch] = useState('');
    const [visibleVehicles, setVisibleVehicles] = useState([]);
    useEffect(() => {
        let list = [];
        if (search.length > 1) {
            Object.keys(assetNames).forEach((a) => {
                if (assetNames[a].regn.toUpperCase().indexOf(search.toUpperCase()) !== -1) {
                    list.push(a);
                }
            });
            setVisibleVehicles([...new Set([...list, ...selectFew])]);
        } else {
            setVisibleVehicles(Object.keys(assetNames));
        }
    }, [assetNames, search, selectFew]);

    const allAssetIds = useMemo(() => Object.keys(assetNames || {}), [assetNames]);
    const allUnsharedAssetIds = useMemo(
        () => allAssetIds.filter((assetId) => !sharedAssetsById[assetId]),
        [allAssetIds, sharedAssetsById]
    );
    const allUnsharedSelected =
        allUnsharedAssetIds.length > 0 &&
        allUnsharedAssetIds.every((assetId) => selectFew.indexOf(assetId) !== -1);

    const handleUnshare = (assetId) => {
        if (!activeSubAccount || !activeSubAccount.subuser_id) {
            return;
        }
        dispatch(deleteShare({ subuser_id: activeSubAccount.subuser_id, assetIds: [assetId] }));
    };

    const renderVehicleCards = (vehicleIds, shared) => {
        if (!assetNames || vehicleIds.length === 0) {
            return null;
        }

        return vehicleIds.map((assetId) => {
            const asset = assetNames[assetId];
            if (!asset) {
                return null;
            }

            const mergedAsset = shared && sharedAssetsById[assetId]
                ? { ...asset, ...sharedAssetsById[assetId] }
                : asset;

            return (
                <SubAccountVehicleCard
                    asset={mergedAsset}
                    shared={shared}
                    selected={selectFew.indexOf(assetId) !== -1}
                    onSelect={handleSelectFew}
                    onUnshare={handleUnshare}
                    key={`asset-${shared ? 'shared' : 'unshared'}-${assetId}`}
                />
            );
        });
    };

    useEffect(() => {
        if (activeSubAccount && activeSubAccount.subuser_id && reloadAccounts && reloadAssets) {
            dispatch(getSubAccountDetails({ subuser_id: activeSubAccount.subuser_id }));
        }
    }, [reloadAssets, reloadAccounts, activeSubAccount, dispatch]);

    const toggleSelectAll = () => {
        if (allUnsharedSelected) {
            setSelectFew([]);
        } else {
            dispatch(selectActiveAsset(null));
            setSelectFew(allUnsharedAssetIds);
        }
    };

    const visibleSharedVehicles = visibleVehicles.filter((assetId) => sharedAssetsById[assetId]);
    const visibleUnsharedVehicles = visibleVehicles.filter((assetId) => !sharedAssetsById[assetId]);
    const hasSelection = selectFew.length > 0;

    const handleClose = () => {
        dispatch(changeView('main'));
    };

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ className: classes.dialogPaper }}
        >
            <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.titleRow}>
                    <Box className={classes.headerLeft}>
                        <Box>
                            <Typography variant="h6" className={classes.titleText}>
                                Select Vehicles to share
                            </Typography>
                            <Typography className={classes.subtitleText}>
                                {activeSubAccount?.person_name || 'Sub Account'}
                            </Typography>
                        </Box>
                    </Box>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <TextField
                            className={classes.searchField}
                            size="small"
                            variant="outlined"
                            placeholder="Search vehicles"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon path={mdiMagnify} size={0.8} color="#64748b" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton onClick={handleClose} className={classes.closeButton}>
                            <Icon path={mdiClose} size={0.8} color="currentColor" />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent className={classes.content}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Box className={classes.panel}>
                            <Box className={classes.panelHeader}>
                                <Typography className={classes.panelTitle}>Not Shared Vehicles</Typography>
                                <div className={classes.panelHeaderActions}>
                                    <Button
                                        size="small"
                                        onClick={toggleSelectAll}
                                        variant="outlined"
                                        className={classes.selectAllButton}
                                        startIcon={
                                            allUnsharedSelected ? (
                                                <CheckBoxOutlineBlankOutlinedIcon />
                                            ) : (
                                                <CheckBoxOutlinedIcon />
                                            )
                                        }
                                        color={allUnsharedSelected ? 'warning' : 'error'}
                                        disabled={allUnsharedAssetIds.length === 0}
                                    >
                                        {allUnsharedSelected ? 'Deselect All' : 'Select All'}
                                    </Button>
                                </div>
                            </Box>
                            <Divider />
                            <Box className={classes.chipsGrid}>
                                {renderVehicleCards(visibleUnsharedVehicles, false)}
                                {visibleUnsharedVehicles.length === 0 && (
                                    <Typography className={classes.emptyText}>
                                        No unshared vehicles found.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {hasSelection ? (
                            <Box className={classes.panel}>
                                <Box className={classes.panelHeader}>
                                    <Typography className={classes.panelTitle}>
                                        Sharing Options
                                    </Typography>
                                    <IconButton
                                        onClick={() => setSelectFew([])}
                                        className={classes.panelCloseButton}
                                        size="small"
                                    >
                                        <Icon path={mdiClose} size={0.75} color="currentColor" />
                                    </IconButton>
                                </Box>
                                <Divider />
                                <SubAccountShareAllVehicles
                                    assets={assetNames}
                                    subAccount={activeSubAccount}
                                    selectFew={selectFew}
                                />
                            </Box>
                        ) : (
                            <Box className={classes.panel}>
                                <Box className={classes.panelHeader}>
                                    <Typography className={classes.panelTitle}>Shared Vehicles</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {visibleSharedVehicles.length} vehicles
                                    </Typography>
                                </Box>
                                <Divider />
                                <Box className={classes.chipsGrid}>
                                    {renderVehicleCards(visibleSharedVehicles, true)}
                                    {visibleSharedVehicles.length === 0 && (
                                        <Typography className={classes.emptyText}>
                                            No shared vehicles found.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default SubAccountVehiclesList;
